// ----------------------------------------------------------------------------
// Section: Tabbed Cards
// ----------------------------------------------------------------------------
// Tab controls render inside each Tab theme block (where block.settings is
// available). The section outputs an empty tablist shell; JS mounts controls
// into it on init. Panels stay in the panels region — panels are not moved.
//
// Below `laptop`, each panel's card grid becomes a Swiper carousel (mirrors
// media-gallery.js / image-and-cards.js); at `laptop`+ it's a plain CSS grid
// and no Swiper instance exists. The section background is a stack of
// per-tab layers that crossfade with the active tab (mirrors the overlay
// pattern in day-night-pack-split-card.liquid, but section-wide). Each
// card's "Learn More" row is a plain instant show/hide (no slide
// animation) — deliberately not the shared accordion helper, which
// animates height; independent per card either way.
// ----------------------------------------------------------------------------

import Swiper from 'swiper';
import { Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/a11y';
import './_tabbed-cards.scss';
var SECTION_SELECTOR = '[data-section-type="tabbed-cards"]';
var TABLIST_SELECTOR = '[data-tabbed-cards-tablist]';
var TABLIST_WRAP_SELECTOR = '[data-tabbed-cards-tablist-wrap]';
var PANELS_SELECTOR = '[data-tabbed-cards-panels]';
var CONTROL_SELECTOR = '[data-tabbed-cards-control]';
var PANEL_SELECTOR = '[data-tabbed-cards-panel]';
var BACKGROUND_SELECTOR = '[data-tabbed-cards-background]';
var CAROUSEL_SELECTOR = '[data-tabbed-cards-carousel]';
var PAGINATION_SELECTOR = '[data-tabbed-cards-pagination]';
var LEARN_MORE_BUTTON_SELECTOR = '[data-tabbed-cards-learn-more-button]';
var ACTIVE_CLASS = 'is-active';
var OPEN_CLASS = 'is-open';
var HAS_TABLIST_CLASS = 'tabbed-cards--has-tablist';
var TONE_LIGHT_CLASS = 'tabbed-cards--tone-light';
var LAPTOP_BREAKPOINT = 1080;
var TABLET_BREAKPOINT = 768;
var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
var PANEL_FADE_OUT_MS = 150;
var PANEL_FADE_IN_MS = 200;
var PANEL_HEIGHT_EASE_MS = 300;
var carouselInstanceIndex = 0;
function mountControlsIntoTablist(section, tablist) {
  var panelsRegion = section.querySelector(PANELS_SELECTOR);
  if (!panelsRegion) {
    return [];
  }
  var controls = Array.from(panelsRegion.querySelectorAll(CONTROL_SELECTOR));
  for (var control of controls) {
    tablist.appendChild(control);
  }
  return controls;
}
function findPanelForControl(section, control) {
  var blockId = control.dataset.tabBlockId;
  if (blockId) {
    var panelByBlockId = section.querySelector("".concat(PANEL_SELECTOR, "[data-tab-block-id=\"").concat(blockId, "\"]"));
    if (panelByBlockId) {
      return panelByBlockId;
    }
  }
  var panelId = control.getAttribute('aria-controls');
  if (!panelId) {
    return null;
  }
  var panel = document.getElementById(panelId);
  if (!panel || !section.contains(panel)) {
    return null;
  }
  return panel;
}
function pairControlsWithPanels(section, controls) {
  return controls.map(control => {
    var panel = findPanelForControl(section, control);
    if (!panel) {
      control.setAttribute('hidden', '');
      return null;
    }
    return {
      control,
      panel
    };
  }).filter(Boolean);
}
function resolveInitialIndex(controls) {
  var hash = window.location.hash.slice(1);
  if (hash) {
    var hashIndex = controls.findIndex(_ref => {
      var {
        panel
      } = _ref;
      return panel.id === hash;
    });
    if (hashIndex >= 0) {
      return hashIndex;
    }
  }
  var defaultOpenIndex = controls.findIndex(_ref2 => {
    var {
      control
    } = _ref2;
    return control.dataset.defaultOpen === 'true';
  });
  if (defaultOpenIndex >= 0) {
    return defaultOpenIndex;
  }
  return 0;
}
function enforceSingleDefaultOpen(controls, activeIndex) {
  var openByDefault = controls.filter(_ref3 => {
    var {
      control
    } = _ref3;
    return control.dataset.defaultOpen === 'true';
  });
  if (openByDefault.length <= 1) {
    return activeIndex;
  }
  var hash = window.location.hash.slice(1);
  if (hash) {
    var hashIndex = controls.findIndex(_ref4 => {
      var {
        panel
      } = _ref4;
      return panel.id === hash;
    });
    if (hashIndex >= 0) {
      return hashIndex;
    }
  }
  return controls.indexOf(openByDefault[0]);
}
function updateControlsState(controls, activeIndex, preserveScroll) {
  var _controls$;
  var scrollY = preserveScroll ? window.scrollY : null;
  var tablistWrap = (_controls$ = controls[0]) === null || _controls$ === void 0 ? void 0 : _controls$.control.closest(TABLIST_WRAP_SELECTOR);
  var tablistTop = tablistWrap ? tablistWrap.getBoundingClientRect().top : 0;
  controls.forEach((_ref5, index) => {
    var {
      control
    } = _ref5;
    var isActive = index === activeIndex;
    control.classList.toggle(ACTIVE_CLASS, isActive);
    control.setAttribute('aria-selected', isActive ? 'true' : 'false');
    control.setAttribute('tabindex', isActive ? '0' : '-1');
  });
  if (preserveScroll && scrollY !== null && tablistWrap) {
    var delta = tablistWrap.getBoundingClientRect().top - tablistTop;
    window.scrollTo(0, scrollY + delta);
  }
}
function updatePanelsState(controls, activeIndex) {
  controls.forEach((_ref6, index) => {
    var {
      panel
    } = _ref6;
    var isActive = index === activeIndex;
    if (isActive) {
      panel.removeAttribute('hidden');
      panel.setAttribute('aria-hidden', 'false');
    } else {
      panel.setAttribute('hidden', '');
      panel.setAttribute('aria-hidden', 'true');
    }
  });
}
function animatePanelSwitch(panelsEl, outgoingPanel, incomingPanel, applySwap) {
  outgoingPanel.style.transition = "opacity ".concat(PANEL_FADE_OUT_MS, "ms ease-out");
  outgoingPanel.style.opacity = '0';
  window.setTimeout(() => {
    var startHeight = panelsEl.getBoundingClientRect().height;
    incomingPanel.style.transition = 'none';
    incomingPanel.style.opacity = '0';
    applySwap();
    var endHeight = panelsEl.getBoundingClientRect().height;
    panelsEl.style.transition = 'none';
    panelsEl.style.height = "".concat(startHeight, "px");
    void panelsEl.offsetHeight;
    outgoingPanel.style.transition = '';
    outgoingPanel.style.opacity = '';
    panelsEl.style.transition = "height ".concat(PANEL_HEIGHT_EASE_MS, "ms ease-out");
    panelsEl.style.height = "".concat(endHeight, "px");
    incomingPanel.style.transition = "opacity ".concat(PANEL_FADE_IN_MS, "ms ease-out");
    incomingPanel.style.opacity = '';
    window.setTimeout(() => {
      panelsEl.style.transition = '';
      panelsEl.style.height = '';
      incomingPanel.style.transition = '';
    }, PANEL_HEIGHT_EASE_MS);
  }, PANEL_FADE_OUT_MS);
}
function updateBackgroundLayer(backgrounds, activePair) {
  var activeBlockId = activePair === null || activePair === void 0 ? void 0 : activePair.control.dataset.tabBlockId;
  backgrounds.forEach(background => {
    background.classList.toggle(ACTIVE_CLASS, background.dataset.tabBlockId === activeBlockId);
  });
}
function updateTextTone(section, activePair) {
  var isLight = (activePair === null || activePair === void 0 ? void 0 : activePair.control.dataset.textTone) === 'light';
  section.classList.toggle(TONE_LIGHT_CLASS, isLight);
}

// -------------------------------------
// Mobile carousel (below `laptop` only)
// -------------------------------------

function initCarouselForPanel(panel, reducedMotion) {
  if (panel._tabbedCardsSwiper) {
    return panel._tabbedCardsSwiper;
  }
  var carouselEl = panel.querySelector(CAROUSEL_SELECTOR);
  if (!carouselEl) {
    return null;
  }
  var paginationEl = panel.querySelector(PAGINATION_SELECTOR);
  var uniqueClass = "tabbed-cards-pagination-".concat(carouselInstanceIndex++);
  paginationEl === null || paginationEl === void 0 || paginationEl.classList.add(uniqueClass);
  var swiper = new Swiper(carouselEl, {
    modules: [Pagination, A11y],
    slidesPerView: 1.25,
    spaceBetween: -1,
    loop: false,
    watchOverflow: true,
    slidesOffsetBefore: 16,
    slidesOffsetAfter: 16,
    roundLengths: true,
    speed: reducedMotion ? 0 : 300,
    pagination: paginationEl ? {
      el: ".".concat(uniqueClass),
      clickable: true
    } : false,
    a11y: {
      enabled: true
    },
    breakpoints: {
      [TABLET_BREAKPOINT]: {
        slidesPerView: 'auto'
      }
    }
  });
  panel._tabbedCardsSwiper = swiper;
  return swiper;
}
function destroyCarouselForPanel(panel) {
  var swiper = panel._tabbedCardsSwiper;
  if (!swiper) {
    return;
  }
  swiper.destroy(true, true);
  panel._tabbedCardsSwiper = null;
}
function createCarouselManager(section, reducedMotion) {
  var mobileQuery = window.matchMedia("(max-width: ".concat(LAPTOP_BREAKPOINT - 1, "px)"));
  var activePanel = null;
  mobileQuery.addEventListener('change', event => {
    if (event.matches) {
      if (activePanel) {
        var swiper = initCarouselForPanel(activePanel, reducedMotion);
        swiper === null || swiper === void 0 || swiper.update();
        swiper === null || swiper === void 0 || swiper.slideTo(0, 0);
      }
    } else {
      section.querySelectorAll(PANEL_SELECTOR).forEach(destroyCarouselForPanel);
    }
  });
  return {
    activate(panel) {
      activePanel = panel || null;
      if (!panel || !mobileQuery.matches) {
        return;
      }
      var swiper = initCarouselForPanel(panel, reducedMotion);
      swiper === null || swiper === void 0 || swiper.update();
      swiper === null || swiper === void 0 || swiper.slideTo(0, 0);
    }
  };
}
function focusTabFromHash(section, controls, setActive) {
  var hash = window.location.hash.slice(1);
  if (!hash) {
    return;
  }
  var targetIndex = controls.findIndex(_ref7 => {
    var {
      panel
    } = _ref7;
    return panel.id === hash;
  });
  if (targetIndex < 0) {
    return;
  }
  if (!section.contains(document.getElementById(hash))) {
    return;
  }
  setActive(targetIndex, true);
}
function handleTabKeydown(event, controls, activeIndex, setActive) {
  var {
    key
  } = event;
  var lastIndex = controls.length - 1;
  var nextIndex = activeIndex;
  if (key === 'ArrowRight' || key === 'ArrowDown') {
    nextIndex = activeIndex >= lastIndex ? 0 : activeIndex + 1;
  } else if (key === 'ArrowLeft' || key === 'ArrowUp') {
    nextIndex = activeIndex <= 0 ? lastIndex : activeIndex - 1;
  } else if (key === 'Home') {
    nextIndex = 0;
  } else if (key === 'End') {
    nextIndex = lastIndex;
  } else {
    return;
  }
  event.preventDefault();
  setActive(nextIndex, true);
  controls[nextIndex].control.focus();
}

// -------------------------------------
// Learn More (plain instant show/hide)
// -------------------------------------

function bindLearnMoreToggles(section) {
  section.querySelectorAll(LEARN_MORE_BUTTON_SELECTOR).forEach(button => {
    var content = document.getElementById(button.getAttribute('aria-controls'));
    if (!content) {
      return;
    }
    button.addEventListener('click', () => {
      var isOpen = button.getAttribute('aria-expanded') === 'true';
      button.classList.toggle(OPEN_CLASS, !isOpen);
      button.setAttribute('aria-expanded', String(!isOpen));
      content.hidden = isOpen;
    });
  });
}
function initSingleTabSection(section) {
  var panels = Array.from(section.querySelectorAll(PANEL_SELECTOR));
  for (var control of section.querySelectorAll(CONTROL_SELECTOR)) {
    control.setAttribute('hidden', '');
  }
  bindLearnMoreToggles(section);
  if (panels.length !== 1) {
    return;
  }
  var [panel] = panels;
  panel.removeAttribute('hidden');
  panel.setAttribute('aria-hidden', 'false');
}
function initSection(section) {
  var tablist = section.querySelector(TABLIST_SELECTOR);
  if (!tablist) {
    initSingleTabSection(section);
    return;
  }
  bindLearnMoreToggles(section);
  var reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches;
  var backgrounds = Array.from(section.querySelectorAll(BACKGROUND_SELECTOR));
  var panelsEl = section.querySelector(PANELS_SELECTOR);
  var carouselManager = createCarouselManager(section, reducedMotion);
  var mountedControls = mountControlsIntoTablist(section, tablist);
  var controls = pairControlsWithPanels(section, mountedControls);
  section.classList.add(HAS_TABLIST_CLASS);
  if (controls.length === 1) {
    section.classList.add('tabbed-cards--single-tab');
  }
  var activeIndex = resolveInitialIndex(controls);
  activeIndex = enforceSingleDefaultOpen(controls, activeIndex);
  var setActive = function setActive(index) {
    var preserveScroll = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    var previousPair = controls[activeIndex];
    activeIndex = index;
    var activePair = controls[activeIndex];
    updateControlsState(controls, activeIndex, preserveScroll);
    var applySwap = () => {
      updatePanelsState(controls, activeIndex);
      updateBackgroundLayer(backgrounds, activePair);
      updateTextTone(section, activePair);
      carouselManager.activate(activePair === null || activePair === void 0 ? void 0 : activePair.panel);
    };
    var canAnimate = !reducedMotion && panelsEl && previousPair && activePair && previousPair.panel !== activePair.panel;
    if (canAnimate) {
      animatePanelSwitch(panelsEl, previousPair.panel, activePair.panel, applySwap);
    } else {
      applySwap();
    }
  };
  setActive(activeIndex);
  if (controls.length > 1) {
    controls.forEach((_ref8, index) => {
      var {
        control
      } = _ref8;
      control.addEventListener('click', () => {
        setActive(index, true);
      });
      control.addEventListener('keydown', event => {
        handleTabKeydown(event, controls, activeIndex, setActive);
      });
    });
  }
  window.addEventListener('hashchange', () => {
    focusTabFromHash(section, controls, setActive);
  });
}
function initTabbedCards() {
  var container = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : document;
  container.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
}
document.addEventListener('DOMContentLoaded', () => {
  initTabbedCards();
});
document.addEventListener('shopify:section:load', _ref9 => {
  var {
    target
  } = _ref9;
  initTabbedCards(target);
});